<!--
  =====================================================================
  SECTION HTML TEXT - DYNAMIC TEXT CONTENT WITH DOCUMENTS
  =====================================================================
  
  Flexible section component for displaying HTML text content.
  
  FEATURES:
  - Rich HTML content rendering (sanitized)
  - Responsive hero image with lazy loading
  - Progressive document list loading (5 items + load more)
  - Historical document separation (current vs archive)
  - External links section
  - Year column display when available
  - File size information display
  
  BEHAVIOR:
  - Hero image centered and scaled on hover
  - Documents sorted by visibility (current first, historical after)
  - "Load more" shows historical docs or next batch
  - Links styled differently from downloads
  - Grid layout adapts to viewport width
  
  =====================================================================
-->

<template>
  <section class="html-text-section">
    <div class="container">
      
      <div v-if="content.eyebrow" class="eyebrow">{{ content.eyebrow }}</div>

      <h2 v-if="content.title" class="title">{{ content.title }}</h2>

      <div v-if="firstImage" class="hero-image">
        <img :src="firstImage" :alt="content.title || 'Immagine'" loading="lazy" />
      </div>

      <div v-if="content.text" class="html-content" v-html="sanitizeHtml(content.text)"></div>

      <div v-if="content.files?.length" class="documents-section">
        <div class="document-list">
          <div
            v-for="(item, index) in visibleDocuments"
            :key="'file-' + index"
            class="document-item"
            :class="{ 'no-year': !hasYearColumn }"
            @click="handleDownloadClick($event, item.fileUrl)"
          >
            <div v-if="hasYearColumn" class="document-year">{{ item.year }}</div>
            <h3 class="document-title">{{ item.label }}</h3>
            <div class="document-size">{{ item.size }}</div>
            <div class="document-icon">
              <img :src="item.icon || pdfFallback" alt="PDF" />
            </div>
          </div>

          <div v-if="hasMoreDocuments" class="load-more-container">
            <button
              @click="loadMoreDocuments"
              class="load-more-button"
              :disabled="isLoading"
            >
              {{ isLoading ? 'Caricamento…' : getLoadMoreText() }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="content.links?.length" class="links-section">
        <div class="document-list">
          <div
            v-for="(link, index) in content.links"
            :key="'link-' + index"
            class="document-item link-item"
            @click="handleLinkClick($event, link.url)"
          >
            <h3 class="document-title">{{ link.titolo }}</h3>
            <div class="document-size">link</div>
            <div class="document-icon">
              <img :src="link.icon || linkFallback" alt="Link" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue'
import { sanitizeHtmlRegex } from '@/utils/sanitizeHtml'

// ───────────────────────────────────────────────────────────────────
// PROPS DEFINITION
// ───────────────────────────────────────────────────────────────────

/**
 * Component props definition
 * @prop {Object} content - Content object containing title, text, files, links, eyebrow, image
 */
const props = defineProps({
  content: { type: Object, required: true }
})

// ───────────────────────────────────────────────────────────────────
// SANITIZATION UTILITY
// ───────────────────────────────────────────────────────────────────

/**
 * Sanitizes HTML content for safe rendering
 * @param {String} html - HTML string to sanitize
 * @returns {String} Sanitized HTML
 */
function sanitizeHtml(html) {
  return sanitizeHtmlRegex(html || '')
}

// ───────────────────────────────────────────────────────────────────
// COMPUTED PROPERTIES - IMAGES
// ───────────────────────────────────────────────────────────────────

/**
 * Extracts first image from content
 * Handles both string and array formats
 */
const firstImage = computed(() => {
  const img = props.content?.image
  if (Array.isArray(img)) {
    return img.length > 0 ? img[0] : null
  }
  return img || null
})

// ───────────────────────────────────────────────────────────────────
// CONSTANTS - FALLBACK ICONS
// ───────────────────────────────────────────────────────────────────

const pdfFallback  = '/images/logos/pdf_icon.png'
const linkFallback = '/images/logos/external_link.svg'

// ───────────────────────────────────────────────────────────────────
// REACTIVE STATE - PROGRESSIVE LOADING
// ───────────────────────────────────────────────────────────────────

// [RIMOSSO] Caricamento progressivo - mostra tutti i documenti
const isLoading = ref(false)
const showHistorical = ref(false)

// ───────────────────────────────────────────────────────────────────
// COMPUTED PROPERTIES - DOCUMENT FILTERING
// ───────────────────────────────────────────────────────────────────

/**
 * Checks if any documents have historical flag
 */
const hasHistoricalDocuments = computed(() => {
  return props.content?.files?.some(item => item.historical === 1) || false
})

/**
 * Filters current (non-historical) documents
 */
const currentDocuments = computed(() => {
  return props.content?.files?.filter(item => !item.historical || item.historical === 0) || []
})

/**
 * Filters historical documents
 */
const historicalDocuments = computed(() => {
  return props.content?.files?.filter(item => item.historical === 1) || []
})

/**
 * Checks if any document has year information
 */
const hasYearColumn = computed(() => {
  return props.content?.files?.some(item => item.year) || false
})

/**
 * Returns currently visible documents based on state
 * - If historical docs exist: shows current docs until "historical" is clicked
 * - Otherwise: shows all documents
 */
const visibleDocuments = computed(() => {
  if (hasHistoricalDocuments.value) {
    if (showHistorical.value) {
      return props.content?.files || []
    } else {
      return currentDocuments.value
    }
  }
  return props.content?.files || []
})

/**
 * Determines if "load more" button should be shown
 * Only shows "Storico" button when historical documents exist
 */
const hasMoreDocuments = computed(() => {
  return hasHistoricalDocuments.value && !showHistorical.value && historicalDocuments.value.length > 0
})

// ───────────────────────────────────────────────────────────────────
// TEXT & BUTTON LABEL GENERATORS
// ───────────────────────────────────────────────────────────────────

/**
 * Generates "load more" button text
 * Only returns "Storico" label
 * @returns {String} Button label
 */
const getLoadMoreText = () => {
  return 'Storico'
}

// ───────────────────────────────────────────────────────────────────
// EVENT HANDLERS - DOCUMENT INTERACTION
// ───────────────────────────────────────────────────────────────────

/**
 * Handles "load more" button click
 * Shows historical documents
 */
const loadMoreDocuments = async () => {
  isLoading.value = true
  await new Promise(resolve => setTimeout(resolve, 300))
  showHistorical.value = true
  isLoading.value = false
}

/**
 * Opens PDF document in new tab
 * @param {Event} e - Click event
 * @param {String} link - Document URL
 */
function handleDownloadClick(e, link) {
  window.open(link, '_blank')
}

/**
 * Handles external link navigation
 * Internal links navigate in same window
 * External links open in new tab
 * @param {Event} e - Click event
 * @param {String} url - Link URL
 */
function handleLinkClick(e, url) {
  if (url && url.startsWith('/')) {
    window.location.href = url
  } else {
    window.open(url, '_blank')
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/partials/variables';
@import '@/assets/styles/partials/compat';
@import '@/assets/styles/partials/typography';

.html-text-section {
  padding: $space-0-5 0 $section-padding-y 0;
  color: $clr-brand-gray;

  @media (max-width: 768px) {
    padding: $space-0-5 0 $section-padding-y-mobile 0;
  }
}

.html-text-section > .container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 $container-padding-x;
  box-sizing: border-box;

  @media (max-width: 1300px) { padding: 0 $container-padding-x; }
  @media (max-width: 1024px) { padding: 0 $space-1-5; }
  @media (max-width: 920px)  { padding: 0 $space-1-5; }
  @media (max-width: 768px)  { padding: 0 $space-1; }
  @media (max-width: 480px)  { padding: 0 $space-1; }
  @media (max-width: 360px)  { padding: 0 $space-0-75; }
}

.eyebrow {
  position: relative;
  display: inline-block;
  color: $clr-tertiary;
  padding-left: $space-2;
  font-size: $font-eyebrow;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: $space-2;
  white-space: nowrap;
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: -50px;
    width: 40%;
    height: 12px;
    background-color: rgba($clr-tertiary, 0.7);
    transition: all 0.5s ease;
  }

  @media (hover: hover) {
    &:hover::before {
      background: rgba($clr-tertiary, 0.8);
      width: 120%;
      transform: scaleY(1.2);
    }
  }

  @media (max-width: 1300px) { font-size: $font-eyebrow-lg; }
  @media (max-width: 920px)  {
    font-size: $font-eyebrow-sm;
    padding-left: 30px;
    &::before { height: 8px; left: -30px; }
  }
  @media (max-width: 480px) {
    font-size: $font-eyebrow-xs; padding-left: 20px; margin-bottom: $space-0-25;
    &::before { height: 7px; left: -20px; }
  }
  @media (max-width: 360px) {
    font-size: $font-eyebrow-xxs; padding-left: 15px;
    &::before { height: 6px; left: -15px; }
  }
}

.title {
  font-size: $font-title;
  font-weight: 300;
  color: $clr-brand-gray;
  margin-bottom: $space-2;
  line-height: 1.2;

  @media (max-width: 1300px) { font-size: $font-title-lg; }
  @media (max-width: 920px)  { font-size: $font-title-sm; margin-bottom: $space-1; }
  @media (max-width: 480px)  { font-size: $font-title-xs; margin-bottom: $space-0-5; }
  @media (max-width: 360px)  { font-size: $font-title-xxs; margin-bottom: $space-0-5; }
}

.hero-image {
  width: 100%;
  margin: 0 auto $space-3 auto;
  text-align: center;

  img {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 500px;
    display: inline-block;
    box-shadow: 0 4px 16px rgba($clr-brand-dark, 0.1);
    transition: all 0.3s ease;

    @media (hover: hover) {
      &:hover {
        transform: scale(1.02);
        box-shadow: 0 8px 24px rgba($clr-brand-dark, 0.15);
      }
    }
  }

  @media (max-width: 1300px) { margin-bottom: $space-2-5; }
  @media (max-width: 920px)  { margin-bottom: $space-2; img { max-width: 100%; } }
  @media (max-width: 480px)  { margin-bottom: $space-1-5; }
  @media (max-width: 360px)  { margin-bottom: $space-1-25; }
}

.html-content {
  font-size: $font-base;
  line-height: $line-height-base;
  color: $clr-brand-gray;

  :deep(p) { margin-bottom: $paragraph-spacing; }
  :deep(ul), :deep(ol) { margin: $list-spacing 0; padding-left: $list-indent; }
  :deep(li) { margin-bottom: $space-0-75; }
  :deep(strong) { font-weight: 700; color: $clr-brand-gray; }
  :deep(em) { font-style: italic; }
  
  /* Link styling - no underline, tertiary green color */
  :deep(a) {
    color: $clr-tertiary;
    text-decoration: none;
  }

  @media (max-width: 1300px) { font-size: $font-base-lg; }
  @media (max-width: 920px)  { font-size: $font-base-sm; }
  @media (max-width: 480px)  { font-size: $font-base-xs; }
  @media (max-width: 360px)  { font-size: $font-base-xxs; }
}

/* Sezione documenti */
.documents-section {
  margin-top: $space-3;
}

/* Sezione links esterni (rimosso border-top) */
.links-section {
  margin-top: $space-3;
  // padding-top: $space-3;
}

/* Titoli sezioni in $clr-brand-dark */
.list-heading {
  width: 100%;
  max-width: 1600px;
  padding: 0 clamp($space-0-5, 2vw, $space-1);
  margin: 0 auto $space-1-5 auto;
  font-size: $font-h4;
  font-weight: 600;
  color: $clr-brand-gray;
  // text-transform: uppercase;
  letter-spacing: 0.5px;
  box-sizing: border-box;
}

.document-list {
  width: 100%;
  max-width: 1600px;
  padding: 0 clamp($space-0-5, 2vw, $space-1);
  overflow-x: hidden;
  box-sizing: border-box;
  margin: 0 auto;
}

.document-item {
  display: grid;
  grid-template-columns: clamp(100px, 15vw, 130px) minmax(0, 1fr) clamp(80px, 10vw, 100px) 40px;
  grid-template-areas: "year title size icon";
  align-items: center;
  gap: clamp(2rem, 3vw, 2.5rem);
  padding: clamp(1rem, 2.5vw, 1.5rem) 0;
  border-bottom: 1px solid $clr-border;
  transition: background-color 0.25s ease, transform 0.2s ease;
  cursor: pointer;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  &.no-year {
    grid-template-columns: minmax(0, 1fr) clamp(80px, 10vw, 100px) 40px;
    grid-template-areas: "title size icon";
  }

  /* 3 colonne per title, label 'link' (size) e icona */
  &.link-item {
    grid-template-columns: minmax(0, 1fr) auto 40px;
    grid-template-areas: "title size icon";
  }

  @media (hover: hover) {
    &:hover {
      background-color: rgba($clr-bg-light, 0.6);
      transform: translateX(4px);
      .document-title { color: $clr-brand-dark; }
    }
  }

  .document-year {
    grid-area: year;
    font-size: $font-list-date;
    color: $clr-text-light;
    font-weight: 500;
    white-space: nowrap;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Nomi documenti/link in $clr-tertiary */
  .document-title {
    grid-area: title;
    font-size: $font-list-title;
    font-weight: 600;
    color: $clr-tertiary;
    line-height: 1.4;
    margin: 0;
    transition: color 0.25s ease;
    cursor: pointer !important;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    @include line-clamp(2, 1.4, $overflow: false);
    word-break: break-word;
    overflow-wrap: break-word;
    padding-right: 1.5rem;
  }

  .document-size {
    grid-area: size;
    font-size: $font-list-meta;
    color: $clr-text-light;
    font-weight: 500;
    white-space: nowrap;
    text-align: right;
    margin-right: clamp(0.5rem, 1vw, 1rem);
  }

  .document-icon {
    grid-area: icon;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    img {
      /* dimensione base icone (link) */
      width: clamp(24px, 3.2vw, 28px);
      height: clamp(24px, 3.2vw, 28px);
      object-fit: contain;
    }
  }
}

/* SOLO PDF (sezione documenti): ancora più grande */
.documents-section .document-icon img {
  width: clamp(30px, 4vw, 36px);
  height: clamp(30px, 4vw, 36px);
}

.load-more-container {
  width: 100%;
  max-width: 1600px;
  padding: 0 clamp($space-0-5, 2vw, $space-1);
  margin-top: clamp(1.5rem, 3vw, 2.5rem);
  display: flex;
  justify-content: flex-start;
  box-sizing: border-box;
}

.load-more-button {
  background: none;
  border: none;
  color: $clr-brand-dark;
  font-size: $font-list-meta;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  padding: clamp(0.75rem, 1.5vw, 1rem) clamp(1rem, 2vw, 1.5rem);
  border-radius: 4px;
  transition: all 0.3s ease;
  position: relative;

  @media (hover: hover) {
    &:hover:not(:disabled) {
      color: $clr-tertiary;
      background-color: rgba($clr-tertiary, 0.1);
    }
  }

  &:disabled {
    color: $clr-text-light;
    cursor: not-allowed;
  }

  &:focus {
    outline: 2px solid $clr-tertiary;
    outline-offset: 2px;
  }
}

@media (max-width: 1300px) {
  .document-list { padding: 0 clamp($space-0-5, 1.5vw, $space-0-75); }
  .load-more-container { padding: 0 clamp($space-0-5, 1.5vw, $space-0-75); }

  .document-item {
    grid-template-columns: clamp(90px, 12vw, 120px) minmax(0, 1fr) clamp(70px, 8vw, 90px) 35px;
    gap: clamp(1.5rem, 2.5vw, 2rem);
    padding: clamp(0.9rem, 2.2vw, 1.3rem) 0;

    &.no-year {
      grid-template-columns: minmax(0, 1fr) clamp(70px, 8vw, 90px) 35px;
    }

    &.link-item {
      grid-template-columns: minmax(0, 1fr) auto 35px;
      grid-template-areas: "title size icon";
    }

    .document-year { font-size: $font-list-date-lg; }
    .document-title { font-size: $font-list-title-lg; }
    .document-size { font-size: $font-list-meta-lg; }
  }

  /* override PDF anche a questo breakpoint */
  .documents-section .document-icon img {
    width: clamp(30px, 4vw, 36px);
    height: clamp(30px, 4vw, 36px);
  }
}

@media (max-width: 1024px) {
  .document-list { padding: 0 clamp($space-0-5, 1.2vw, $space-0-75); }
  .load-more-container { padding: 0 clamp($space-0-5, 1.2vw, $space-0-75); }

  .document-item {
    grid-template-columns: auto minmax(0, 1fr) auto;
    grid-template-areas:
      "year title icon"
      "size size size";
    gap: clamp(1.5rem, 2.5vw, 2rem);
    padding: clamp(0.8rem, 2vw, 1.2rem) 0;

    &.no-year {
      grid-template-columns: minmax(0, 1fr) auto;
      grid-template-areas:
        "title icon"
        "size size";
    }

    /* title+icon in riga, 'link' sotto a tutta larghezza */
    &.link-item {
      grid-template-columns: minmax(0, 1fr) auto;
      grid-template-areas:
        "title icon"
        "size size";
    }

    .document-size {
      margin-top: clamp(0.25rem, 1vw, 0.5rem);
      grid-column: 1 / -1;
      text-align: left;
      margin-right: 0;
    }

    .document-year { min-width: clamp(80px, 15vw, 100px); }
  }
}

@media (max-width: 920px) {
  .document-list { padding: 0 clamp($space-0-25, 1vw, $space-0-5); }

  .document-item {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      "title icon"
      "year year"
      "size size";
    gap: clamp(1.5rem, 2vw, 1.75rem);
    padding: clamp(0.7rem, 1.8vw, 1rem) 0;

    &.no-year {
      grid-template-areas:
        "title icon"
        "size size";
    }

    &.link-item {
      grid-template-areas:
        "title icon"
        "size size";
    }

    .document-icon {
      justify-self: end;
      align-self: start;
    }

    .document-year, .document-size {
      margin-top: clamp(0.2rem, 0.8vw, 0.4rem);
      grid-column: 1 / -1;
    }

    .document-year { font-size: $font-list-date-sm; min-width: auto; }
    .document-title { font-size: $font-list-title-sm; line-height: 1.3; }
    .document-size { font-size: $font-list-meta-sm; }

    .document-icon img {
      width: 22px;
      height: 22px;
    }
  }

  .documents-section .document-icon img {
    width: 28px;
    height: 28px;
  }

  .load-more-button { font-size: $font-list-meta-sm; }
}

@media (max-width: 640px) {
  .document-list { padding: 0 clamp($space-0-25, 0.8vw, $space-0-5); }

  .document-item {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      "title icon"
      "year year"
      "size size";
    gap: clamp(1.5rem, 1.5vw, 1.75rem);
    padding: clamp(0.6rem, 1.5vw, 0.9rem) 0;

    &.no-year {
      grid-template-areas:
        "title icon"
        "size size";
    }

    &.link-item {
      grid-template-areas:
        "title icon"
        "size size";
    }

    .document-year, .document-size {
      grid-column: 1 / -1;
      margin-top: clamp(0.2rem, 0.6vw, 0.4rem);
    }

    .document-icon {
      justify-self: end;
      img {
        width: 20px;
        height: 20px;
      }
    }
  }

  .documents-section .document-icon img {
    width: 24px;
    height: 24px;
  }
}

@media (max-width: 480px) {
  .load-more-container {
    padding: 0 clamp($space-0-25, 0.6vw, 0.25rem);
    margin-top: clamp(0.75rem, 2vw, 1.5rem);
  }

  .document-item {
    padding: clamp(0.5rem, 1.2vw, 0.7rem) 0;
    gap: clamp(1rem, 1vw, 1.25rem);

    .document-year { font-size: $font-list-date-xs; }
    .document-title {
      font-size: $font-list-title-xs;
      line-height: 1.2;
      @include line-clamp-lines(3, 1.2);
    }
    .document-size { font-size: $font-list-meta-xs; }

    .document-icon img {
      width: 18px;
      height: 18px;
    }
  }

  .documents-section .document-icon img {
    width: 22px;
    height: 22px;
  }

  .load-more-button { font-size: $font-list-meta-xs; }
}

@media (max-width: 360px) {
  .document-list { padding: 0 $space-0-25; }

  .document-item {
    padding: clamp(0.4rem, 1vw, 0.6rem) 0;
    gap: clamp(1rem, 0.8vw, 1.25rem);

    .document-year { font-size: $font-list-date-xxs; }
    .document-title { font-size: $font-list-title-xxs; line-height: 1.1; }
    .document-size { font-size: $font-list-meta-xxs; }

    .document-icon img {
      width: 16px;
      height: 16px;
    }
  }

  .documents-section .document-icon img {
    width: 20px;
    height: 20px;
  }

  .load-more-button { font-size: $font-list-meta-xxs; }
}
</style>